import { useCallback } from "react";
import { AgentColumnType } from "type/agent-column-type";

/**
 * Persists the Team table's visible-column selection per user in localStorage so the
 * preference survives page reloads and future sessions (Redux state resets on reload).
 * Keyed by the user's agentId. Returns the persisted column-id list (or null when absent
 * or invalid) and a setter that writes the current selection.
 */
// v2: bump invalidates column selections persisted during earlier iterations whose column set /
// order differed, so everyone re-seeds from the current default order.
const storageKey = (userId: number) => `teamTableColumns:v2:${userId}`;

export const useColumnPrefs = (userId: number) => {
	const readColumnPrefs = useCallback((): string[] | null => {
		try {
			const raw = localStorage.getItem(storageKey(userId));
			if (!raw) {
				return null;
			}
			const parsed = JSON.parse(raw);
			if (!Array.isArray(parsed)) {
				return null;
			}
			// Drop ids that are no longer valid column types (e.g. after a refactor).
			const valid = parsed.filter((id): id is string => typeof id === "string" && !!AgentColumnType.getById(id));
			return valid.length ? valid : null;
		} catch(e) {
			return null;
		}
	}, [userId]);

	const writeColumnPrefs = useCallback((columns: AgentColumnType[]) => {
		try {
			const ids = columns.map((column) => column.id);
			localStorage.setItem(storageKey(userId), JSON.stringify(ids));
		} catch(e) {
			// localStorage unavailable (private mode / quota) — preference simply won't persist.
		}
	}, [userId]);

	return { readColumnPrefs, writeColumnPrefs };
};
